'use client';

import React, { useState } from 'react';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Search, MessageSquare, Settings, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CommandAgent, CommandAgentList } from '@/types/command-agent';
import { fetchData } from '@/utils/api-handler';
import { useProtectedQuery } from '@/hooks/use-protected-query';
import { formatDateTime, SEARCH_DEBOUNCE_WAIT } from '@/utils/global';
import { useRouter } from 'next/navigation';

interface AllAgentsSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onAgentSelect?: (agentId: string) => void;
    currentAgentId?: string | null;
}

export function AllAgentsSheet({ open, onOpenChange, onAgentSelect, currentAgentId }: AllAgentsSheetProps) {
    const router = useRouter();
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });

    const { data, isLoading } = useProtectedQuery<CommandAgentList>({
        queryKey: ['command-agents-sheet', debouncedSearch],
        queryFn: () => {
            const params = new URLSearchParams({
                search: debouncedSearch,
                page: '1',
                sort_by: 'created_at',
                sort_order: 'desc',
                limit: '50',
            });
            return fetchData(`/api/command-team/agents?${params.toString()}`);
        },
        enabled: open,
        requiredPermissions: ['LIST_COMMAND_AGENTS'],
    });

    const agents = data?.items || [];

    const handleChat = (agent: CommandAgent) => {
        onAgentSelect?.(agent.id);
        onOpenChange(false);
    };

    const handleSettings = (agent: CommandAgent) => {
        router.push(`/workforce/command-team/agents/${agent.id}?name=${encodeURIComponent(agent.name)}`);
        onOpenChange(false);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
                <SheetHeader className="pb-4">
                    <SheetTitle className="text-lg font-semibold">Command Agents</SheetTitle>
                    <p className="text-sm text-muted-foreground">Manage and deploy your digital workforce</p>
                </SheetHeader>

                <div className="space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search agents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {isLoading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[...Array(4)].map((_, i) => (
                                <Skeleton key={i} className="h-[160px] rounded-xl" />
                            ))}
                        </div>
                    ) : agents.length === 0 ? (
                        <div className="text-center py-12 text-sm text-muted-foreground">
                            No agents found
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {agents.map((agent) => {
                                const isCurrent = agent.id === currentAgentId;
                                const { date } = formatDateTime(agent.created_at);
                                return (
                                    <div
                                        key={agent.id}
                                        className={`rounded-xl border p-4 flex flex-col gap-3 transition-all ${
                                            isCurrent
                                                ? 'border-primary bg-primary/5 shadow-sm'
                                                : 'border-border hover:border-border/80 hover:shadow-sm'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-sm truncate">{agent.name}</span>
                                                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">{date}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                                            {agent.description || 'No description'}
                                        </p>

                                        <div className="flex items-center justify-between mt-auto pt-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${agent.is_active ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                                                <span className="text-[11px] text-muted-foreground">
                                                    {agent.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 cursor-pointer"
                                                    onClick={() => handleChat(agent)}
                                                >
                                                    <MessageSquare className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 cursor-pointer"
                                                    onClick={() => handleSettings(agent)}
                                                >
                                                    <Settings className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
